import type { Product } from '../types';

/**
 * Convert a /uploads/... URL to an /image/{width}/... URL, which serves a
 * pre-generated lossless WebP derivative at that size (see the route at
 * functions/image/[width]/[[path]].ts).
 *
 * Non-/uploads/ URLs (data: URLs from local dev, external images) pass through
 * untouched.
 */
/**
 * Cache generation. Derived responses are served `immutable, max-age=31536000`,
 * so a stale URL cannot be evicted by a purge alone — browsers hold it too.
 * Bumping this mints fresh URLs and retires the old ones instantly.
 *
 * v2: the pre-WebP era served byte-identical URLs, pinned for a year.
 * v3: photos viewed before `images:derive` ran had their heavy PNG cached under
 *     the same year-long header (fixed in functions/image/[width]/[[path]].ts —
 *     underived responses are now short-lived), so those entries needed retiring.
 *
 * Bump again only if derivatives are regenerated with different output.
 */
const IMG_GEN = '3';

export function imgW(url: string | null | undefined, width: number): string {
  if (!url || !url.startsWith('/uploads/')) return url || '';
  const key = url.slice('/uploads/'.length);
  return `/image/${width}/${key}?v=${IMG_GEN}`;
}

/**
 * Full-resolution lossless WebP — same pixels as the original PNG, roughly half
 * the bytes. For the zoomed, view-full-size page where nothing may be lost.
 */
export function imgFull(url: string | null | undefined): string {
  return imgW(url, 99999);
}

/** Standard srcSet across a set of widths. */
export function imgSrcSet(url: string | null | undefined, widths: number[]): string | undefined {
  if (!url || !url.startsWith('/uploads/') || widths.length === 0) return undefined;
  return widths.map((w) => `${imgW(url, w)} ${w}w`).join(', ');
}

/**
 * True when the image this product displays is a transparent cut-out, so it
 * should float on the page instead of sitting in a card.
 *
 * Reads the flag off the same row `primary_image` resolves to — the is_primary
 * image, which is not always images[0] once photos have been reordered.
 */
export function isTransparentProduct(product: Pick<Product, 'images'>): boolean {
  const primary = product.images?.find((i) => i.is_primary) || product.images?.[0];
  return primary?.bg_color === 'transparent';
}
